// Render a voxel model to a small PNG data URL for the collection list. Reuses
// the main WebGLRenderer (no second GL context) but draws a dedicated little
// scene to an offscreen render target.
//
// Two entry points: captureObject() renders an already-built mesh (the deferred
// path — the baked render asset, so no re-mesh on the main thread); capture()
// rebuilds a model from a grid into a PRIVATE temp grid + mesher (the no-worker
// fallback). capture() bulk-copies chunks (O(chunks)) rather than per-cell
// set() — the per-cell copy of a solid 256³ was ~16.7M map writes.

import * as THREE from "three";
import { CELL_SIZE, VoxelGrid } from "../model/voxel-grid";
import { type PaletteLookup, VoxelMesher } from "./voxel-mesh";

const SIZE = 192;
const VIEW_DIR = new THREE.Vector3(0.55, 0.5, 0.85).normalize();

export class Thumbnailer {
  private scene = new THREE.Scene();
  private camera = new THREE.PerspectiveCamera(35, 1, 0.01, 100);
  private mesher: VoxelMesher;
  private temp = new VoxelGrid();
  private rt = new THREE.WebGLRenderTarget(SIZE, SIZE);
  private buf = new Uint8Array(SIZE * SIZE * 4);
  private canvas = document.createElement("canvas");
  private _color = new THREE.Color();
  private _box = new THREE.Box3();
  private _center = new THREE.Vector3();
  private _size = new THREE.Vector3();

  constructor(
    private renderer: THREE.WebGLRenderer,
    palette: PaletteLookup,
  ) {
    this.mesher = new VoxelMesher(palette);
    this.mesher.setGridVisible(false);
    this.mesher.setEdgesVisible(true);
    this.scene.add(this.mesher.object3D);
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.5));
    const key = new THREE.DirectionalLight(0xffffff, 1.1);
    key.position.set(0.5, 1, 0.6);
    this.scene.add(key);
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
  }

  // Render an already-built model mesh (e.g. the baked render asset). The mesh
  // is added to the private scene only for the duration of the render.
  captureObject(obj: THREE.Object3D): string {
    this.scene.add(obj);
    this._box.setFromObject(obj);
    const empty = this._box.isEmpty();
    if (!empty) {
      this._box.getCenter(this._center);
      this._box.getSize(this._size);
      const span = Math.max(this._size.x, this._size.y, this._size.z);
      this.renderFramed(this._center, span);
    }
    this.scene.remove(obj);
    return empty ? "" : this.encode();
  }

  // Returns a PNG data URL, or "" for an empty grid. Rebuilds the model in a
  // private temp grid (mesher.sync) — the synchronous fallback used when the
  // off-thread bake (which feeds captureObject) is unavailable.
  capture(grid: VoxelGrid): string {
    this.temp.clear();
    // Bulk chunk copy (O(chunks)), not per-cell set().
    for (const [ck, chunk] of grid.rawChunks()) {
      this.temp.setRawChunk(ck, chunk.cells.slice(), chunk.count);
    }
    this.temp.markAllChunksDirty();

    // Exact content bounds for framing — one cheap pass (comparisons, no writes).
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (const [ix, iy, iz] of grid.entries()) {
      if (ix < minX) minX = ix;
      if (iy < minY) minY = iy;
      if (iz < minZ) minZ = iz;
      if (ix > maxX) maxX = ix;
      if (iy > maxY) maxY = iy;
      if (iz > maxZ) maxZ = iz;
    }
    if (minX === Infinity) return "";
    this.mesher.sync(this.temp);

    this._center.set(
      ((minX + maxX + 1) / 2) * CELL_SIZE,
      ((minY + maxY + 1) / 2) * CELL_SIZE,
      ((minZ + maxZ + 1) / 2) * CELL_SIZE,
    );
    const span = Math.max(maxX - minX + 1, maxY - minY + 1, maxZ - minZ + 1) * CELL_SIZE;
    this.renderFramed(this._center, span);
    return this.encode();
  }

  // Position the camera at a 3/4 angle framing a sphere of the given world span
  // around `center`, then render the scene to the offscreen target.
  private renderFramed(center: THREE.Vector3, worldSpan: number): void {
    const radius = worldSpan * 0.72;
    const dist = (radius / Math.sin((this.camera.fov * Math.PI) / 360)) * 1.15;
    this.camera.position.copy(center).addScaledVector(VIEW_DIR, dist);
    this.camera.lookAt(center);

    const prevRT = this.renderer.getRenderTarget();
    this.renderer.getClearColor(this._color);
    const prevAlpha = this.renderer.getClearAlpha();
    this.renderer.setRenderTarget(this.rt);
    this.renderer.setClearColor(0x14151f, 1);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.readRenderTargetPixels(this.rt, 0, 0, SIZE, SIZE, this.buf);
    this.renderer.setRenderTarget(prevRT);
    this.renderer.setClearColor(this._color, prevAlpha);
  }

  // Flip the bottom-up GL pixels into the 2D canvas and encode a PNG data URL.
  private encode(): string {
    const ctx = this.canvas.getContext("2d")!;
    const img = ctx.createImageData(SIZE, SIZE);
    const row = SIZE * 4;
    for (let y = 0; y < SIZE; y++) {
      const src = (SIZE - 1 - y) * row;
      img.data.set(this.buf.subarray(src, src + row), y * row);
    }
    ctx.putImageData(img, 0, 0);
    return this.canvas.toDataURL("image/png");
  }
}
